import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";

const UrinalysisSection = ({
  blood,
  onBloodChange,
  glucose,
  onGlucoseChange,
  additionalResults,
  onAdditionalResultsChange,
}) => {
  const isBloodNotCarriedOut =
    String(blood || "")
      .trim()
      .toLowerCase() === "n/a";
  const isGlucoseNotCarriedOut =
    String(glucose || "")
      .trim()
      .toLowerCase() === "n/a";
  const isAdditionalResultsNotCarriedOut =
    String(additionalResults || "")
      .trim()
      .toLowerCase() === "n/a";
  const isUrinalysisNotCarriedOut =
    isBloodNotCarriedOut &&
    isGlucoseNotCarriedOut &&
    isAdditionalResultsNotCarriedOut;

  const handleToggle = (onChange, checked) => {
    onChange({
      target: {
        value: checked ? "n/a" : "",
      },
    });
  };

  const handleUrinalysisToggle = (checked) => {
    handleToggle(onBloodChange, checked);
    handleToggle(onGlucoseChange, checked);
    handleToggle(onAdditionalResultsChange, checked);
  };

  return (
    <div className="px-4 mt-6">
      <div className="rounded-2xl border border-sky-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-sky-100 bg-sky-50 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h2 className="font-fatfrank text-lg md:text-xl text-sas-primary">
              Urinalysis
            </h2>
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className="text-sas-primary"
            />
            <span className="text-xs md:text-sm text-sas-primary-dark font-montserrat">
              Urine can be a useful indicator of a person's health
            </span>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-sas-primary">
              Not carried out
            </span>
            <span className="relative inline-flex h-5 w-10 items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={isUrinalysisNotCarriedOut}
                onChange={(e) => handleUrinalysisToggle(e.target.checked)}
              />
              <span className="absolute inset-0 rounded-full bg-slate-300 transition peer-checked:bg-sas-primary"></span>
              <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition peer-checked:translate-x-5"></span>
            </span>
          </label>
        </div>

        <div className="p-4 grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-sky-50 p-3 text-sas-primary-dark">
              <div className="font-fatfrank text-base md:text-lg flex items-center justify-between gap-3">
                <span>Blood</span>
              </div>
              <div className="text-xs md:text-sm">
                Blood should not normally be present in urine.
              </div>
            </div>
            <input
              type="text"
              className="m-3 mt-2 w-[calc(100%-1.5rem)] rounded-lg border-2 border-sas-primary/40 px-4 py-3 font-semibold text-sas-primary-dark focus:outline-none focus:ring-2 focus:ring-sas-primary/30 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
              value={blood}
              onChange={onBloodChange}
              disabled={isUrinalysisNotCarriedOut}
            />
          </div>

          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-sky-50 p-3 text-sas-primary-dark">
              <div className="font-fatfrank text-base md:text-lg flex items-center justify-between gap-3">
                <span>Glucose</span>
              </div>
              <div className="text-xs md:text-sm">
                Can indicate the onset of diabetes. Glucose is not normally
                excreted through urine.
              </div>
            </div>
            <input
              type="text"
              className="m-3 mt-2 w-[calc(100%-1.5rem)] rounded-lg border-2 border-sas-primary/40 px-4 py-3 font-semibold text-sas-primary-dark focus:outline-none focus:ring-2 focus:ring-sas-primary/30 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
              value={glucose}
              onChange={onGlucoseChange}
              disabled={isUrinalysisNotCarriedOut}
            />
          </div>
        </div>

        <div className="px-4 pb-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <label className="block text-sm font-semibold uppercase tracking-[0.18em] text-sas-primary">
              Additional Results
            </label>
          </div>
          <textarea
            rows="4"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 font-semibold text-sas-primary-dark focus:outline-none focus:ring-2 focus:ring-sas-primary/30 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
            value={additionalResults}
            onChange={onAdditionalResultsChange}
            disabled={isUrinalysisNotCarriedOut}
          />
        </div>
      </div>
    </div>
  );
};

export default UrinalysisSection;
